import { Observable } from 'rxjs';
import { HvlPage, HvlQueryModel, HvlResponse } from '@hvlng/framework-bff/core';
import { map } from 'rxjs/operators';
import { HrRestServiceBase } from './hr-rest-service-base';
import { kovanQueryPageSerializer } from 'ui-shared';
import { Hr } from '../model/hr.configuration.model';

export class HrCrudRestServiceBase<
    TModel extends object,
    TQueryModel extends HvlQueryModel,
> extends HrRestServiceBase {
    private readonly modelClassReference: { new (): TModel } | null;
    private readonly queryModelClassReference: { new (): TQueryModel } | null;

    constructor(
        endpointModule: keyof Hr,
        modelClassReference: { new (): TModel } | null,
        queryModelClassReference: { new (): TQueryModel } | null,
        serviceRootSuffix: string,
    ) {
        super(endpointModule, serviceRootSuffix);
        this.modelClassReference = modelClassReference;
        this.queryModelClassReference = queryModelClassReference;
    }

    public queryList(queryModel: TQueryModel): Observable<Array<TModel>> {
        return this.httpClient
            .post<HvlResponse<Array<TModel>>>(this.serviceRoot + 'query', queryModel)
            .pipe(map((data) => this.converter.deserializeArray(data.body || [], this.modelClassReference)));
    }

    public queryPage(queryModel: TQueryModel): Observable<HvlPage<Array<TModel>>> {
        return this.httpClient
            .post<HvlResponse<HvlPage<TModel[]>>>(
                this.serviceRoot + 'page',
                kovanQueryPageSerializer(queryModel, this.queryModelClassReference),
            )
            .pipe(map((response) => this.getPageFromResponse(response, this.modelClassReference)));
    }

    public getById(id: number): Observable<TModel> {
        return this.httpClient
            .get<HvlResponse<TModel>>(this.serviceRoot + id)
            .pipe(map((data) => this.converter.deserializeObject(data.body, this.modelClassReference)));
    }

    public update(model: TModel): Observable<TModel> {
        return this.httpClient
            .put<HvlResponse<TModel>>(this.serviceRoot, this.converter.serialize(model, this.modelClassReference))
            .pipe(map((data) => this.converter.deserializeObject(data.body, this.modelClassReference)));
    }

    public save(model: TModel): Observable<TModel> {
        return this.httpClient
            .post<HvlResponse<TModel>>(this.serviceRoot, this.converter.serialize(model, this.modelClassReference))
            .pipe(map((data) => this.converter.deserializeObject(data.body, this.modelClassReference)));
    }

    public deleteById(id: number): Observable<any> {
        return this.httpClient.delete(this.serviceRoot + id);
    }
}
